import LockIcon from '@mui/icons-material/Lock';
import UpgradeIcon from '@mui/icons-material/Upgrade';
import { useTheme, alpha } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import React from 'react';
import { Link } from 'react-router-dom';

import { getTierName } from '../../config/featureConfig';
import { SubscriptionTier, subscriptionTierDetails } from '../../models/Subscription';

interface LockedFeatureOverlayProps {
  requiredTier: SubscriptionTier;
  featureName?: string;
  description?: string;
  showUpgradeButton?: boolean;
  onUpgradeClick?: () => void;
  children?: React.ReactNode;
  variant?: 'overlay' | 'card' | 'inline';
}

/**
 * LockedFeatureOverlay Component (HA-108)
 * Displays a locked state overlay or card for tier-restricted features.
 * Shows the required tier, price, and a clear upgrade CTA so users know
 * exactly what plan unlocks the feature.
 */
const LockedFeatureOverlay: React.FC<LockedFeatureOverlayProps> = ({
  requiredTier,
  featureName,
  description,
  showUpgradeButton = true,
  onUpgradeClick,
  children,
  variant = 'overlay',
}) => {
  const theme = useTheme();
  const tierName = getTierName(requiredTier);
  const tierDetails = subscriptionTierDetails.find((t) => t.tier === requiredTier);
  const tierPriceLabel =
    tierDetails && tierDetails.price > 0
      ? `${tierName} plan — $${tierDetails.price}/month`
      : tierName;

  const handleUpgradeClick = () => {
    if (onUpgradeClick) {
      onUpgradeClick();
    }
  };

  if (variant === 'overlay' && children) {
    return (
      <Box sx={{ position: 'relative' }}>
        {/* Blurred/disabled content underneath */}
        <Box
          sx={{
            filter: 'blur(2px)',
            opacity: 0.5,
            pointerEvents: 'none',
            userSelect: 'none',
          }}
        >
          {children}
        </Box>
        {/* Overlay */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: alpha(theme.palette.background.paper, 0.85),
            borderRadius: 1,
            p: 3,
          }}
        >
          <LockIcon
            sx={{
              fontSize: 48,
              color: theme.palette.warning.main,
              mb: 2,
            }}
          />
          <Typography
            variant="h6"
            gutterBottom
            sx={{
              textAlign: 'center',
            }}
          >
            {featureName || 'Feature Locked'}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              textAlign: 'center',
              mb: 1.5,
            }}
          >
            {description || `This feature is part of the ${tierPriceLabel}.`}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              color: 'text.secondary',
              mb: 2,
              fontWeight: 600,
              letterSpacing: 0.4,
              textTransform: 'uppercase',
            }}
          >
            Requires {tierPriceLabel}
          </Typography>
          {showUpgradeButton && (
            <Button
              component={Link}
              to="/subscription?tab=plans"
              variant="contained"
              color="primary"
              startIcon={<UpgradeIcon />}
              onClick={handleUpgradeClick}
            >
              Upgrade to {tierName}
            </Button>
          )}
        </Box>
      </Box>
    );
  }

  if (variant === 'card') {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 4,
          textAlign: 'center',
          backgroundColor: alpha(theme.palette.grey[100], 0.5),
          border: `1px dashed ${theme.palette.divider}`,
          borderRadius: 2,
        }}
      >
        <LockIcon
          sx={{
            fontSize: 48,
            color: theme.palette.warning.main,
            mb: 2,
          }}
        />
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>
          {featureName || 'Feature Locked'}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            maxWidth: 520,
            mx: 'auto',
            mb: 2,
          }}
        >
          {description ||
            `This feature is part of the ${tierPriceLabel}. Upgrade to unlock ${(
              featureName || 'this feature'
            ).toLowerCase()} for your practice.`}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            color: 'text.secondary',
            mb: 2.5,
            fontWeight: 600,
            letterSpacing: 0.4,
            textTransform: 'uppercase',
          }}
        >
          Requires {tierPriceLabel}
        </Typography>
        {showUpgradeButton && (
          <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              component={Link}
              to="/subscription?tab=plans"
              variant="contained"
              color="primary"
              size="medium"
              startIcon={<UpgradeIcon />}
              onClick={handleUpgradeClick}
            >
              Upgrade to {tierName}
            </Button>
            <Button
              component={Link}
              to="/subscription?tab=plans"
              variant="text"
              size="medium"
              onClick={handleUpgradeClick}
            >
              Compare Plans
            </Button>
          </Box>
        )}
      </Paper>
    );
  }

  // Inline variant
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 1,
        color: theme.palette.text.secondary,
      }}
    >
      <LockIcon fontSize="small" sx={{ color: theme.palette.warning.main }} />
      <Typography variant="body2" component="span">
        Requires {tierName}
      </Typography>
      {showUpgradeButton && (
        <Button
          component={Link}
          to="/subscription"
          variant="text"
          size="small"
          onClick={handleUpgradeClick}
          sx={{ ml: 0.5 }}
        >
          Upgrade
        </Button>
      )}
    </Box>
  );
};

export default LockedFeatureOverlay;
